import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';

import { ConfigService } from './config.service';
import { ConfigSettings } from 'environments/ConfigSettings';
import * as Entities from '@shared/entities';

export interface IPropDictionary {
  [key: string]: string;
}

export interface IMeasurementDictionary {
  [key: string]: number;
}

export interface IAppInsightService {
  trackPageView(options?: {
    pageName?: string;
    url?: string;
    props?: IPropDictionary;
    measurements?: IMeasurementDictionary;
  }): void;
  trackEvent(
    eventName: string,
    options?: {
      props?: IPropDictionary;
      measurements?: IMeasurementDictionary;
    }
  ): void;
  trackException(
    ex: Error,
    options?: {
      handledAt?: string;
      props?: IPropDictionary;
      measurements?: IMeasurementDictionary;
    }
  ): void;
  trackTrace(message: string, options?: { props?: IPropDictionary; measurements?: IMeasurementDictionary }): void;
}

@Injectable({ providedIn: 'root' })
export class AppInsightsService {
  applicationHostDomain = '';
  AppInsights!: ApplicationInsights;
  app = '';
  private config?: ConfigSettings;
  private initialized = false;
  private userContext: IPropDictionary = {};

  constructor(
    private location: Location,
    private configurationService: ConfigService
  ) {
    this.configurationService.Config.subscribe((config) => {
      if (!config || this.initialized) return;
      this.config = config;
      this.app = config.appName;
      this.AppInsights = new ApplicationInsights({
        config: {
          instrumentationKey: config.instrumentationKey,
          disableFetchTracking: false,
          enableAutoRouteTracking: false,
          disablePageUnloadEvents: ['unload'],
        },
      });
      this.AppInsights.loadAppInsights();
      this.initialized = true;
      if (this.userContext['UserId']) {
        this.AppInsights.setAuthenticatedUserContext(this.userContext['UserId']);
      }
    });
  }

  trackPageView(options?: {
    pageName?: string;
    url?: string;
    props?: IPropDictionary;
    measurements?: IMeasurementDictionary;
    routeInfo?: ActivatedRouteSnapshot;
  }): void {
    if (!this.initialized) {
      return;
    }
    options = options || {};
    options.pageName = options.pageName || this.getDefaultPageName();
    const url = options.url || this.getDefaultUrl();

    options.props = this.getProps(options.props, options.routeInfo);
    options.measurements = this.getMeasurements(options.measurements);

    this.AppInsights.trackPageView({
      name: options.pageName,
      uri: url,
      properties: options.props,
      measurements: options.measurements,
    });
  }

  trackEvent(
    eventName: string,
    options?: {
      props?: IPropDictionary;
      measurements?: IMeasurementDictionary;
      routeInfo?: ActivatedRouteSnapshot;
    }
  ): void {
    if (!this.initialized) {
      return;
    }
    options = options || {};
    options.props = this.getProps(options.props, options.routeInfo);
    options.measurements = this.getMeasurements(options.measurements);

    this.AppInsights.trackEvent({ name: eventName }, { ...options.props, ...options.measurements });
  }

  trackException(
    ex: Error,
    options?: {
      handledAt?: string;
      props?: IPropDictionary;
      measurements?: IMeasurementDictionary;
    }
  ): void {
    if (!this.initialized) {
      return;
    }
    options = options || {};
    this.AppInsights.trackException({ exception: ex }, this.getProps(options.props));
  }

  trackTrace(message: string, options?: { props?: IPropDictionary }): void {
    if (!this.initialized) {
      return;
    }
    options = options ?? {};
    options.props = this.getProps(options.props);
    this.AppInsights.trackTrace({ message, severityLevel: SeverityLevel.Warning }, options.props);
  }

  setAuthenticatedUser(user: Entities.User | null | undefined): void {
    if (!user) {
      this.clearAuthenticatedUser();
      return;
    }

    const userId = String(user.userId);
    const roles =
      user.roles
        ?.map((role) => role.name)
        .filter(Boolean)
        .join(',') ?? '';
    const programIds =
      user.programs
        ?.map((program) => program.programId)
        .filter(Boolean)
        .join(',') ?? '';

    this.userContext = this.removeEmptyProperties({
      UserId: userId,
      UserName: user.userName,
      Email: user.emailAddress,
      Roles: roles,
      RetailerId: user.retailerId === undefined ? '' : String(user.retailerId),
      ProgramIds: programIds,
    });

    if (this.initialized) {
      this.AppInsights.setAuthenticatedUserContext(userId);
    }
  }

  clearAuthenticatedUser(): void {
    this.userContext = {};

    if (this.initialized) {
      this.AppInsights.clearAuthenticatedUserContext();
    }
  }

  setHostDomain(host: string): void {
    this.applicationHostDomain = host;
    console.debug('Host Domain', this.applicationHostDomain);
  }

  private getProps(props?: IPropDictionary, routeInfo?: ActivatedRouteSnapshot): IPropDictionary {
    props = props || {};

    let defaults: IPropDictionary = {
      App: this.app,
      Client: this.config?.client ?? '',
      Environment: this.config?.environmentName ?? '',
    };

    if (routeInfo) {
      defaults['RoutePath'] = routeInfo.routeConfig?.path ?? '';

      if (routeInfo && routeInfo.data) {
        defaults['RouteTitle'] = routeInfo.data['title'] ?? '';
      }

      Object.keys(routeInfo.params).forEach((key) => {
        defaults['PathParam_' + key] = routeInfo.params[key];
      });
    }

    defaults = this.removeEmptyProperties(defaults);
    props = this.removeEmptyProperties(props);
    props = { ...defaults, ...this.userContext, ...props };
    return props;
  }

  private removeEmptyProperties(props: IPropDictionary): IPropDictionary {
    const cleanProps: IPropDictionary = {};

    Object.entries(props).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        cleanProps[key] = String(value);
      }
    });

    return cleanProps;
  }

  private getMeasurements(measurements?: IMeasurementDictionary): IMeasurementDictionary {
    return measurements ?? {};
  }

  private getDefaultPageName(): string {
    return 'CRS Page';
  }

  private getDefaultUrl(): string {
    let path = this.location.path() || '';
    const idx = path.lastIndexOf('/');
    if (idx === path.length - 1) {
      // If path ends in slash, get rid of it
      path = path.substring(0, path.length - 1);
    }
    return path;
  }
}
